<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("shipping_return") }}
                    </h6>
                    <nav
                        aria-label="breadcrumb"
                        class="d-none d-md-inline-block ml-md-4"
                    >
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>

                <div class="col-lg-6 col-5 text-right">

					<base-button size="md" type="neutral" v-if="params.id != 0" @click="showSaveModal = true">{{
						$t("save")
					}}</base-button>
                </div>
            </div>
        </base-header>

        <div class="row">
            <div class="container-fluid mt--6 col-lg-5">
                <div>

					
                    <card
                        header-classes="bg-transparent"
                        footer-classes="bg-transparent"
                        body-classes="px-lg-4 py-lg-4"
                        class=" border-0  mb-4"
                    >

					<div class="row">
						<div class="col-md-6">
							<base-input :label="$t('platform')">
								<el-select
									v-model="platform_id"
									:label="$t('platform')"
									:disabled="lock || !hasWebcam"
								>
									<el-option
										v-for="option in PlatformTable"
										:key="option.id"
										:label="option.name"
										:value="option.id"
									>
									</el-option>
								</el-select>
							</base-input>
						</div>
					</div>
                        <div class="row">
                            <div class="col-md-12">
                                <base-input
                                    :placeholder="$t('scanbarcode')"
                                    v-model="barcode"
									:label="$t('Barcode')"
                                    class="form-control-alternative"
                                    prepend-icon="ni ni-zoom-split-in"
                                    v-on:keyup.enter="handleBarcode"
                                    :disabled="!hasWebcam"
                                ></base-input>
                            </div>
                        </div>
						
						<!-- Camera Settings Section -->
						<div class="row">
							<div class="col-md-12 mt-3">
								<card
									header-classes="bg-transparent"
									body-classes="px-lg-4 py-lg-4"
									class="border-0 mb-4"
								>
									<template slot="header">
										<div class="d-flex justify-content-between align-items-center flex-wrap">
											<h4 class="mb-0">{{ $t("camera_settings") }}</h4>
											<div class="header-controls d-flex align-items-center">
												<div class="connection-status mr-3">
													<span v-if="!hasWebcam" class="badge badge-warning">
														<i class="fa fa-exclamation-triangle mr-1"></i>
														No Webcam
													</span>
													<span v-else-if="isRecording" class="badge badge-danger">
														<i class="fa fa-circle mr-1 recording-pulse"></i>
														Recording
													</span>
													<span v-else-if="isWebcamActive" class="badge badge-success">
														<i class="fa fa-circle mr-1"></i>
														Camera Connected
													</span>
													<span v-else class="badge badge-secondary">
														<i class="fa fa-circle mr-1"></i>
														Camera Disconnected
													</span>
												</div>
												
												<!-- Recording Controls -->
												<div class="recording-controls" v-if="hasWebcam && isWebcamActive">
													<base-button
														v-if="!isRecording"
														@click="startVideoRecording"
														type="danger"
														size="sm"
													>
														<i class="fa fa-circle mr-2"></i>
														Start Recording
													</base-button>
													<base-button
														v-if="isRecording"
														@click="stopVideoRecording"
														type="warning"
														size="sm"
													>
														<i class="fa fa-stop mr-2"></i>
														Stop & Save ({{ recordingDuration }})
													</base-button>
												</div>
											</div>
										</div>
									</template>
									
									<div class="webcam-container">
										<!-- Webcam Display -->
										<div class="webcam-display-area mb-3">
											<div v-if="hasWebcam" class="video-container">
												<video
													ref="webcamVideo"
													width="640"
													height="480"
													autoplay
													muted
													playsinline
													v-show="isWebcamActive"
													:class="{'webcam-video': true, 'recording': isRecording}"
												></video>
												
												<!-- Recording indicator overlay -->
												<div v-if="isRecording" class="recording-indicator">
													<i class="fa fa-circle recording-pulse"></i>
													<span>REC {{ recordingDuration }}</span>
												</div>
											</div>
											
											<div v-if="!hasWebcam" class="webcam-placeholder">
												<i class="fa fa-exclamation-triangle fa-3x text-warning"></i>
												<p class="text-warning mt-2"><strong>No Webcam Detected</strong></p>
												<p class="text-muted">Please connect a webcam and refresh the page</p>
											</div>
											
											<div v-else-if="!isWebcamActive" class="webcam-placeholder">
												<i class="fa fa-video-camera fa-3x text-muted"></i>
												<p class="text-muted mt-2">Click "Connect" to start camera preview</p>
											</div>
										</div>
										
										<!-- Camera Selection and Quality Settings -->
										<div class="camera-selection-section mb-4" :class="{'disabled-section': !hasWebcam}">
											<div class="row">
												<div class="col-md-6">
													<base-input>
														<el-select
															v-model="selectedCameraId"
															@change="onCameraChange"
															placeholder="Choose camera device"
															:disabled="!hasWebcam"
														>
															<el-option
																v-for="camera in availableCameras"
																:key="camera.deviceId"
																:label="camera.label || `Camera ${camera.deviceId.substring(0, 8)}...`"
																:value="camera.deviceId"
															>
															</el-option>
														</el-select>
													</base-input>
												</div>
												<div class="col-md-3">
													<base-input>
														<el-select
															v-model="selectedVideoQuality"
															@change="onVideoQualityChange"
															placeholder="Select quality"
															:disabled="!hasWebcam"
														>
															<el-option
																label="2K - Default"
																value="2k"
															>
															</el-option>
															<el-option
																label="4K"
																value="4k"
															>
															</el-option>
														</el-select>
													</base-input>
												</div>
												<div class="col-md-3">
													<label class="form-control-label">&nbsp;</label>
													<base-button
														@click="connectCamera"
														:type="isWebcamActive ? 'success' : 'info'"
														size="md"
														class="w-100 text-nowrap"
														:disabled="!hasWebcam"
													>
														<i :class="isWebcamActive ? 'fa fa-check-circle' : 'fa fa-plug'" class="mr-1"></i>{{ isWebcamActive ? 'Connected' : 'Connect' }}
													</base-button>
												</div>
											</div>
											
											<!-- Quality Info -->
											<div class="row mt-3" v-if="hasWebcam && selectedVideoQuality">
												<div class="col-md-12">
													<div class="alert" :class="isRecording ? 'alert-danger' : 'alert-info'">
														<small>
															<i :class="isRecording ? 'fa fa-circle recording-pulse mr-2' : 'fa fa-info-circle mr-2'"></i>
															<span v-if="isRecording">
																<strong>RECORDING</strong> - {{ getQualityDisplayText() }} - {{ recordingDuration }}
															</span>
															<span v-else>
																Current Quality: {{ getQualityDisplayText() }}
															</span>
														</small>
													</div>
												</div>
											</div>
											
											<!-- No Webcam Message -->
											<div class="row mt-3" v-if="!hasWebcam">
												<div class="col-md-12">
													<div class="alert alert-warning">
														<small>
															<i class="fa fa-exclamation-triangle mr-2"></i>
															Camera features are disabled. No webcam detected.
														</small>
													</div>
												</div>
											</div>
										</div>
									</div>
								</card>
							</div>
						</div>
						
						<hr/>
						<div class="row">
							<div class="col-md-12">
								<!-- router to /backoffice/packings/shipping/return -->
								<router-link
									:to="{ name: 'ReportOrderReturn' }"
									class="btn btn-primary"
								>
									{{ $t("report_order_return") }}
								</router-link>
							</div>
						</div>
                    </card>
                </div>
            </div>

            <div class="container-fluid mt--6 col-lg-7">
                <div>
                    <!-- card right -->
                    <card
                        class="no-border-card"
                        body-classes="px-0 pb-1"
                        footer-classes="pb-2"
                    >
                        <template slot="header">
                            <div class="row">
                                <label
                                    class="col-md-6 col-form-label form-control-label"
                                >
                                    <h3
                                        class="mb-0"
                                        v-if="params.id == '' || params.id == 0"
                                    >
                                        {{ $t("order_information") }}
                                    </h3>
                                    <h3 class="mb-0" v-else>
                                        {{ $t("order_information") }} :
                                        {{ this.params.id }}
                                    </h3>

                                    <h3 v-if="params.tracking_number != ''">
                                        Tracking :
                                        {{ this.params.tracking_number }}
                                    </h3>
                                </label>
                            </div>
                        </template>
                        <div>
                            <!-- Selected Product -->
                            <div class="row">
                                <div
                                    class="col-lg-3"
                                    v-for="(o, index) in order_items"
                                    :key="o.id"
                                >
                                    <div class="card">
                                        <img
                                            class="card-img-top"
                                            :src="
                                                GetProductImage(
                                                    o.wms_product_id
                                                )
                                            "
                                            style="max-height:150px; width:150px;margin-left: auto; margin-right: auto;"
                                        />
                                        <div class="card-body">
                                            <h3>
                                                <span>
                                                    {{ o.wms_product_name }}
                                                </span>
                                                (
                                                <span class="text-green">{{ o.pickup_quantity }} </span>
                                                )
                                            </h3>

                                            <!-- Broken -->
											 <hr>
											 <div class="col-12">ชำรุด</div>
                                            <div class="col-md-12">
                                                <base-button
                                                    @click.native="
                                                        handleBrokenAdd(index)
                                                    "
													:disabled="o.good_quantity == 0"
                                                    class="edit"
                                                    type="warning"
                                                    size="sm"
                                                    icon
                                                >
                                                    <i
                                                        class="text-dtac-bt fa fa-plus"
                                                    ></i>
                                                </base-button>

                                                <input
                                                    type="text"
                                                    class="form-control text-center"
                                                    style="display:inline; width:30%; margin-right:10px"
													disabled
                                                    :value="o.broken_quantity"
                                                />

                                                <base-button
                                                    @click.native="
                                                        handleBrokenRemove(
                                                            index
                                                        )
                                                    "
													:disabled="o.broken_quantity == 0"
                                                    class="edit"
                                                    type="warning"
                                                    size="sm"
                                                    icon
                                                >
                                                    <i
                                                        class="text-dtac-bt fa fa-minus"
                                                    ></i>
                                                </base-button>
                                            </div>

                                            <!-- Lost -->
											 <hr>
											 <div class="col-12">สูญหาย</div>
											 
                                            <div class="col-md-12">
                                                <base-button
                                                    @click.native="
                                                        handleLostAdd(index)
                                                    "
													:disabled="o.good_quantity == 0"
                                                    class="edit"
                                                    type="warning"
                                                    size="sm"
                                                    icon
                                                >
                                                    <i
                                                        class="text-dtac-bt fa fa-plus"
                                                    ></i>
                                                </base-button>

                                                <input
                                                    type="text"
                                                    class="form-control text-center"
                                                    style="display:inline; width:30%; margin-right:10px"
													disabled
                                                    :value="o.lost_quantity"
                                                />

                                                <base-button
                                                    @click.native="
                                                        handleLostRemove(index)
                                                    "
													:disabled="o.lost_quantity == 0"
                                                    class="edit"
                                                    type="warning"
                                                    size="sm"
                                                    icon
                                                >
                                                    <i
                                                        class="text-dtac-bt fa fa-minus"
                                                    ></i>
                                                </base-button>
                                            </div>

											<!-- Other -->
											<hr>
											<div class="col-12">อื่น ๆ</div>
											
										   <div class="col-md-12">
											   <base-button
												   @click.native="
													   handleOtherAdd(index)
												   "
												   :disabled="o.good_quantity == 0"
												   class="edit"
												   type="warning"
												   size="sm"
												   icon
											   >
												   <i
													   class="text-dtac-bt fa fa-plus"
												   ></i>
											   </base-button>

											   <input
												   type="text"
												   class="form-control text-center"
												   style="display:inline; width:30%; margin-right:10px"
												   disabled
												   :value="o.other_quantity"
											   />

											   <base-button
												   @click.native="
													   handleOtherRemove(index)
												   "
												   :disabled="o.other_quantity == 0"
												   class="edit"
												   type="warning"
												   size="sm"
												   icon
											   >
												   <i
													   class="text-dtac-bt fa fa-minus"
												   ></i>
											   </base-button>
										   </div>
                                        </div>
                                    </div>
                                </div>
                            </div> <!-- End Product -->


                        </div>
                    </card>
                </div>
            </div>
        </div>

        <!--  Modal -->
        <modal :show.sync="showModal" modal-classes="modal-secondary">
            <form class="new-event--form" @submit.prevent="saveEvent">
                Loading...
            </form>

            <template slot="footer"> </template>
        </modal>

		<modal
            :show.sync="showSaveModal"
            modal-classes="modal-secondary modal-lg"
        >
		<div class="row">
                <div class="col-md-4">
					สินค้า
                </div>
                <div class="col-md-2 text-center">
					ดี
                </div>
                <div class="col-md-2 text-center">
					ชำรุด
                </div>
                <div class="col-md-2 text-center">
					สูญหาย
                </div>
                <div class="col-md-2 text-center">
					อื่นๆ
                </div>
		</div>
		<hr />
            <div class="row" v-for=" (item,index) in order_items" :key="index">
					<!-- show product good , broken loss -->
                <div class="col-md-4">
					{{  item.wms_product_name }}
                </div>
                <div class="col-md-2 text-center">
					{{  item.good_quantity }}
                </div>
                <div class="col-md-2 text-center">
					{{  item.broken_quantity }}
                </div>
                <div class="col-md-2 text-center">
					{{  item.lost_quantity }}
                </div>
                <div class="col-md-2 text-center">
					{{  item.other_quantity }}
                </div>
            </div>
			<hr/>
			<div class="row">
				<div class="col-md-12">
					<div class="form-group">
						<base-input
							v-model="note"
							placeholder="หมายเหตุ"
							label="หมายเหตุ"
							class="form-control-alternative"></base-input>
					</div>
				</div>
			</div>

			<template slot="footer">
				<button
					type="button"
					class="btn btn-md btn-primary"
					@click="handleSave()"
				>
					{{ $t("save") }}
				</button>
                <button
                    type="button"
                    class="btn btn-md btn-link ml-auto"
                    @click="showSaveModal = false"
                >
                    {{ $t("close") }}
                </button>
            </template>
		</modal>

    </div>
</template>
<script>
import { Table, TableColumn, Select, Option } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import clientPaginationMixin from "../PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapGetters, mapActions } from "vuex";
import FileInput from "@/components/Inputs/FileInput";
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import moment from "moment";
import __WEBPACK_EXTERNAL_MODULE__0__ from "vue-flatpickr-component";
import OrderTables from "./OrderTables.vue";

export default {
    mixins: [clientPaginationMixin],
    components: {
        flatPicker,
        FileInput,
        BasePagination,
        RouteBreadCrumb,
        [Select.name]: Select,
        [Option.name]: Option,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn
    },
    computed: {
        ...mapGetters({
            StorageTableOrg: "WmsStorage/getData",
            ProductTable: "WmsProduct/getData",
            OrderTable: "Order/getData",
			PlatformTable: "Order/getPlatformTable"
        }),

        StorageTable() {
            return this.StorageTableOrg.filter(item => {
                return item.ID != 1;
            });
        }
    },
    data() {
        return {
            isLoading: false,
            showModal: false,
			showSaveModal: false,
            lock: false,
            barcode: "",
			platform_id:1,
            note: "",
			orders: [],
			order:null,
            order_items: [],
			params : {
				id:0,
				tracking_number:"",
			},
			// Webcam variables
			isWebcamActive: false,
			webcamStream: null,
			availableCameras: [],
			selectedCameraId: '',
			selectedVideoQuality: '2k', // Default to 2K
			hasWebcam: true, // Track if webcam is available
			webcamConfigs: {
				'2k': {
					width: 2560,
					height: 1440,
					frameRate: 30
				},
				'4k': {
					width: 3840,
					height: 2160,
					frameRate: 30
				}
			},
			// Video recording variables
			isRecording: false,
			mediaRecorder: null,
			recordedChunks: [],
			recordingStartTime: null,
			recordingDuration: '00:00',
			recordingTimer: null,
			// Current order info for recording
			currentOrderId: '',
			currentBarcode: ''
        };
    },
    mounted() {
        this.listProduct();
        this.clearParam();
		this.checkWebcamAvailability();
    },
	beforeDestroy() {
		// Clean up webcam stream when component is destroyed
		this.stopWebcam();
	},
    methods: {
        ...mapActions({
            createReturn: "Packing/createReturn",
            closeReturn: "Packing/closeReturn",
            listProduct: "WmsProduct/listData",
            listOrder: "Order/listData",
			orderReturn: "Order/setOrderReturn"
        }),

		// Webcam methods
		async startWebcam() {
			try {
				// Stop any existing stream first
				if (this.webcamStream) {
					this.webcamStream.getTracks().forEach(track => track.stop());
				}

				const currentConfig = this.webcamConfigs[this.selectedVideoQuality];
				const constraints = {
					video: {
						width: { ideal: currentConfig.width, min: 640 },
						height: { ideal: currentConfig.height, min: 480 },
						frameRate: { ideal: currentConfig.frameRate, min: 15 }
					}
				};

				// Use selected camera if available
				if (this.selectedCameraId) {
					constraints.video.deviceId = { exact: this.selectedCameraId };
				}

				console.log('Requesting webcam with constraints:', constraints);
				
				try {
					this.webcamStream = await navigator.mediaDevices.getUserMedia(constraints);
				} catch (error) {
					console.warn('High quality constraints failed, trying with fallback constraints:', error);
					// Fallback to basic constraints if exact fails
					const fallbackConstraints = {
						video: {
							width: { ideal: 1280, min: 640 },
							height: { ideal: 720, min: 480 },
							frameRate: { ideal: 30, min: 15 }
						}
					};
					if (this.selectedCameraId) {
						fallbackConstraints.video.deviceId = { exact: this.selectedCameraId };
					}
					this.webcamStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
				}
				
				// Get actual video track settings
				const videoTrack = this.webcamStream.getVideoTracks()[0];
				const settings = videoTrack.getSettings();
				
				console.log('Video track settings:', settings);
				console.log('Video track capabilities:', videoTrack.getCapabilities());
				
				// Ensure track is enabled
				videoTrack.enabled = true;
				
				// Set video element source
				const videoElement = this.$refs.webcamVideo;
				videoElement.srcObject = this.webcamStream;
				
				// Wait for video to load
				await new Promise((resolve) => {
					videoElement.addEventListener('loadedmetadata', resolve, { once: true });
				});
				
				console.log('Video element ready:', {
					videoWidth: videoElement.videoWidth,
					videoHeight: videoElement.videoHeight,
					currentTime: videoElement.currentTime,
					duration: videoElement.duration
				});
				
				this.isWebcamActive = true;
				console.log(`✓ Webcam active (${settings.width}x${settings.height}@${settings.frameRate}fps)`);
				
			} catch (error) {
				console.error('Error accessing webcam:', error);
				
				// Try fallback with basic constraints
				try {
					const fallbackConstraints = {
						video: true
					};

					console.log('Trying fallback constraints:', fallbackConstraints);
					this.webcamStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
					
					const videoTrack = this.webcamStream.getVideoTracks()[0];
					console.log('Fallback video track settings:', videoTrack.getSettings());
					
					this.$refs.webcamVideo.srcObject = this.webcamStream;
					this.isWebcamActive = true;
				} catch (fallbackError) {
					console.error('Fallback webcam access failed:', fallbackError);
					swal.fire({
						title: 'Error',
						text: 'Could not access webcam. Please check permissions and camera selection.',
						icon: 'error',
						confirmButtonText: this.$t("ok")
					});
				}
			}
		},

		stopWebcam() {
			// Stop recording if active
			if (this.isRecording) {
				this.stopVideoRecording();
			}
			
			if (this.webcamStream) {
				this.webcamStream.getTracks().forEach(track => track.stop());
				this.webcamStream = null;
			}
			this.isWebcamActive = false;
		},

		// Connect camera function
		async connectCamera() {
			if (this.isWebcamActive) {
				// If already connected, disconnect
				this.stopWebcam();
			} else {
				// Connect and start preview
				await this.getCameraDevices();
				await this.startWebcam();
			}
		},

		// Video quality change handler
		async onVideoQualityChange() {
			// Always connect with new quality settings
			await this.connectCamera();
			
			swal.fire({
				title: 'Quality Changed',
				text: `Camera connected with ${this.selectedVideoQuality.toUpperCase()} quality (${this.getQualityDisplayText()})`,
				icon: 'success',
				timer: 2000,
				showConfirmButton: false
			});
		},

		// Get quality display text
		getQualityDisplayText() {
			const config = this.webcamConfigs[this.selectedVideoQuality];
			return `${config.width}x${config.height} @ ${config.frameRate}fps`;
		},

		// Video recording methods
		startVideoRecording() {
			if (!this.webcamStream) {
				console.error('No webcam stream available');
				return;
			}

			this.recordedChunks = [];
			this.recordingStartTime = moment();
			
			// Setup MediaRecorder with quality-specific settings
			let options = null;
			const supportedTypes = [
				'video/webm;codecs=vp9',
				'video/webm;codecs=vp8', 
				'video/webm',
				'video/mp4'
			];

			for (const type of supportedTypes) {
				if (MediaRecorder.isTypeSupported(type)) {
					console.log('Using supported type:', type);
					options = { mimeType: type };
					
					// Set bitrate based on quality
					if (type.includes('webm')) {
						if (this.selectedVideoQuality === '4k') {
							options.videoBitsPerSecond = 25000000; // 25 Mbps for 4K
						} else {
							options.videoBitsPerSecond = 10000000; // 10 Mbps for 2K
						}
					}
					break;
				}
			}
			
			try {
				this.mediaRecorder = new MediaRecorder(this.webcamStream, options);
				console.log('MediaRecorder created with options:', options);
				
				this.mediaRecorder.ondataavailable = (event) => {
					if (event.data.size > 0) {
						this.recordedChunks.push(event.data);
					}
				};

				this.mediaRecorder.onstop = () => {
					this.saveVideoFile();
				};

				this.mediaRecorder.onerror = (event) => {
					console.error('MediaRecorder error:', event.error);
					swal.fire({
						title: 'Recording Error',
						text: 'Error during recording: ' + event.error,
						icon: 'error',
						confirmButtonText: 'OK'
					});
				};

				this.mediaRecorder.start(100);
				this.isRecording = true;
				this.startDurationTimer();
				
				console.log('Video recording started at', this.selectedVideoQuality.toUpperCase(), 'quality');
				
			} catch (error) {
				console.error('Failed to create MediaRecorder:', error);
				swal.fire({
					title: 'Recording Error',
					text: 'Cannot create video recorder. Please try a different browser.',
					icon: 'error',
					confirmButtonText: 'OK'
				});
			}
		},

		stopVideoRecording() {
			if (this.mediaRecorder && this.isRecording) {
				this.mediaRecorder.stop();
				this.isRecording = false;
				
				if (this.recordingTimer) {
					clearInterval(this.recordingTimer);
					this.recordingTimer = null;
				}
			}
		},

		startDurationTimer() {
			this.recordingTimer = setInterval(() => {
				if (this.recordingStartTime && this.isRecording) {
					const now = moment();
					const duration = moment.duration(now.diff(this.recordingStartTime));
					this.recordingDuration = moment.utc(duration.asMilliseconds()).format('mm:ss');
				}
			}, 1000);
		},

		saveVideoFile() {
			if (this.recordedChunks.length === 0) {
				console.error('No recording chunks available');
				swal.fire({
					title: 'Recording Error',
					text: 'No video data was recorded. Please try again.',
					icon: 'error',
					confirmButtonText: 'OK'
				});
				return;
			}
			
			let mimeType = 'video/webm';
			if (this.mediaRecorder.mimeType) {
				mimeType = this.mediaRecorder.mimeType;
			}
			
			const blob = new Blob(this.recordedChunks, { type: mimeType });
			
			const orderId = this.currentOrderId || 'unknown';
			const barcode = this.currentBarcode || 'unknown';
			
			let extension = 'webm';
			if (mimeType.includes('mp4')) {
				extension = 'mp4';
			}
			
			const filename = `ORDER_${orderId}_${barcode}.${extension}`;
			const recordingEndTime = moment();
			const duration = moment.duration(recordingEndTime.diff(this.recordingStartTime));
			const durationString = moment.utc(duration.asMilliseconds()).format('mm:ss');
			
			// Auto-download file
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			URL.revokeObjectURL(url);
			document.body.removeChild(a);
			
			console.log(`Video saved: ${filename} (${durationString})`);
		},


		// Check webcam availability
		async checkWebcamAvailability() {
			try {
				// Check if mediaDevices API is available
				if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
					this.hasWebcam = false;
					this.showWebcamNotFoundAlert();
					return;
				}

				// Get camera devices
				await this.getCameraDevices();
				
				// Check if any video input devices are available
				if (this.availableCameras.length === 0) {
					this.hasWebcam = false;
					this.showWebcamNotFoundAlert();
				} else {
					this.hasWebcam = true;
				}
			} catch (error) {
				console.error('Error checking webcam availability:', error);
				this.hasWebcam = false;
				this.showWebcamNotFoundAlert();
			}
		},

		// Show webcam not found alert
		showWebcamNotFoundAlert() {
			swal.fire({
				title: this.$t("webcam_not_found"),
				text: 'No webcam detected. Please connect a webcam and refresh the page.',
				icon: "error",
				confirmButtonText: this.$t("ok")
			});
		},

		// Camera device management
		async getCameraDevices() {
			try {
				const devices = await navigator.mediaDevices.enumerateDevices();
				this.availableCameras = devices.filter(device => device.kind === 'videoinput');
				
				// Select first camera by default if none selected
				if (this.availableCameras.length > 0 && !this.selectedCameraId) {
					this.selectedCameraId = this.availableCameras[0].deviceId;
				}
			} catch (error) {
				console.error('Error getting camera devices:', error);
				this.availableCameras = [];
			}
		},


		async onCameraChange() {
			if (this.isWebcamActive) {
				// Restart webcam with new camera
				this.stopWebcam();
				setTimeout(() => {
					this.startWebcam();
				}, 500);
			}
		},

		handleSave(){

			// Reformat
			let params = {
				order_id : this.params.id,
				product_good : [],
				product_broken: [],
				product_lost:[],
				other_reason:[],
				note :this.note 
			} 

			//loop order_items
			for (let i = 0; i < this.order_items.length; i++) {
				let item = this.order_items[i];
				if (item.good_quantity > 0) {
					params.product_good.push({
						wms_product_id: item.wms_product_id,
						wms_product_name: item.wms_product_name,
						barcode: item.barcode,
						pickup_quantity: item.good_quantity,
					});
				}

				if (item.broken_quantity > 0) {
					params.product_broken.push({
						wms_product_id: item.wms_product_id,
						wms_product_name: item.wms_product_name,
						barcode: item.barcode,
						pickup_quantity: item.broken_quantity,
					});
				}

				if (item.lost_quantity > 0) {
					params.product_lost.push({
						wms_product_id: item.wms_product_id,
						wms_product_name: item.wms_product_name,
						barcode: item.barcode,
						pickup_quantity: item.lost_quantity,
					});
				}

				if (item.other_quantity > 0) {
					params.other_reason.push({
						wms_product_id: item.wms_product_id,
						wms_product_name: item.wms_product_name,
						barcode: item.barcode,
						pickup_quantity: item.other_quantity,
					});
				}
			}

			// call api
			this.orderReturn(params).then((res) => {
				console.log("result",res)
				if (res) {
					swal.fire({
						title: this.$t("return_success"),
						text: '',
						icon: "success",
						confirmButtonText: this.$t("ok")
					});
					this.clearParam();
				} else {
					swal.fire({
						title: this.$t("return_fail"),
						text: '',
						icon: "error",
						confirmButtonText: this.$t("ok")
					});
				}
			}).catch((err) => {
				console.log("error",err)
				if (res) {
					swal.fire({
						title: this.$t("return_fail"),
						text: err,
						icon: "error",
						confirmButtonText: this.$t("ok")
					});
				}
			})

		},

		handleLostAdd(index) {
			// check good quantity > 0 , then lost quantity can add good quantity decrease
			if (this.order_items[index].good_quantity > 0) {
				this.order_items[index].good_quantity--;
				this.order_items[index].lost_quantity++;
			}
		},
		handleLostRemove(index) {
			// check lost quantity > 0 , then lost quantity can decrease good quantity increase
			if (this.order_items[index].lost_quantity > 0) {
				this.order_items[index].lost_quantity--;
				this.order_items[index].good_quantity++;
			}
		},

		handleBrokenAdd(index) {
			// check good quantity > 0 , then broken quantity can add good quantity decrease
			if (this.order_items[index].good_quantity > 0) {
				this.order_items[index].good_quantity--;
				this.order_items[index].broken_quantity++;
			}
		},
		handleBrokenRemove(index) {
			if (this.order_items[index].broken_quantity > 0) {
				this.order_items[index].broken_quantity--;
				this.order_items[index].good_quantity++;
			}
		},

		// handleOtherADd
		handleOtherAdd(index) {
			// check good quantity > 0 , then broken quantity can add good quantity decrease
			if (this.order_items[index].good_quantity > 0) {
				this.order_items[index].good_quantity--;
				this.order_items[index].other_quantity++;
			}
		},
		handleOtherRemove(index) {
			if (this.order_items[index].other_quantity > 0) {
				this.order_items[index].other_quantity--;
				this.order_items[index].good_quantity++;
			}
		},

        async handleBarcode() {
            // create variable string of startdate and enddate in format YYYY-MM-DD  , startdate is last 3 days enddate is today
            let startdate = moment()
                .add(-30, "days")
                .format("YYYY-MM-DD");
            let enddate = moment().format("YYYY-MM-DD");

			this.params.tracking_number = ""
			this.params.id  = 0 

            let params = {
                time_from: startdate,
                time_to: enddate,
                tracking_number: this.barcode,
				bo_platform_id: this.platform_id
            };

            await this.listOrder(params);

            let orders = this.OrderTable;

            if (orders.length == 1) {
                // order found
				this.order = orders[0]
				console.log(this.order)
				
				// Check if order is already returned
				if (this.order.order_status === 141) {
					swal.fire({
						title: this.$t("errors.order_already_returned"),
						text: `Order ID: ${this.order.ID} has already been returned`,
						icon: "error",
						confirmButtonText: this.$t("ok")
					});
					this.barcode = ""; // Clear barcode input
					return; // Stop processing
				}
				
				this.params.id = this.order.ID
				this.params.tracking_number = this.barcode
				
				// Store current order info for video recording
				this.currentOrderId = this.order.ID
				this.currentBarcode = this.barcode

                let wms_str = this.order.wms_product_summary;
                let wms = JSON.parse(wms_str);

                let items = [];
                for (let i = 0; i < wms.length; i++) {
                    let item = wms[i];
                    items.push({
                        wms_product_id: item.wms_product_id,
                        wms_product_name: item.wms_product_name,
                        barcode: item.barcode,
                        pickup_quantity: item.pickup_quantity,
                        good_quantity: item.pickup_quantity,
                        broken_quantity: 0,
                        lost_quantity: 0,
						other_quantity:0
                    });
                }

                this.order_items = items;
				
				// Auto-connect camera when order is opened (only if webcam is available)
				if (this.hasWebcam && !this.isWebcamActive) {
					await this.connectCamera();
				}
				
				// Auto-start recording when order is successfully loaded (only if webcam is available)
				if (this.hasWebcam) {
					setTimeout(async () => {
						if (this.isWebcamActive && !this.isRecording) {
							this.startVideoRecording();
						}
					}, 1000); // Wait 1 second for camera to be ready
				}
            } else if (orders.length > 1) {
                // show invalid tracking number
                swal.fire({
                    title: this.$t("errors.invalid_tracking_number"),
                    icon: "error",
                    confirmButtonText: this.$t("ok")
                });
            } else {
                // show alert order not found
                swal.fire({
                    title: this.$t("errors.orders.not_found"),
                    icon: "error",
                    confirmButtonText: this.$t("ok")
                });
            }
        },

        clearParam() {
            // Stop recording when clearing order
            if (this.isRecording) {
                this.stopVideoRecording();
            }
            
            this.order_items = [];
            this.params.id = "";
            this.params.tracking_number = "";
            this.params.barcode = "";
            this.barcode = "";
			this.note = "";
			this.showSaveModal = false;
            this.state = 0;
            
            // Clear current order info for recording
            this.currentOrderId = '';
            this.currentBarcode = '';
        },

        GetProductImage(id) {
            // find product table
            let product = this.ProductTable.find(x => x.ID == id);
            if (product) {
                return (
                    process.env.VUE_APP_API_BASE_URL +
                    "/files/wms/" +
                    product.Image
                );
            } else {
                return "";
            }
        }
    }
};
</script>

<style scoped>
.el-table td {
    padding: 0 !important;
}

.webcam-container {
    text-align: center;
}

.webcam-display-area {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 260px;
    background-color: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
}

.video-container {
    position: relative;
    display: inline-block;
}

.webcam-video {
    border: 2px solid #28a745;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.webcam-video.recording {
    border: 2px solid #dc3545;
    box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

.recording-indicator {
    position: absolute;
    top: 10px;
    left: 10px;
    background: rgba(220, 53, 69, 0.9);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 5px;
}

.webcam-placeholder {
    text-align: center;
    color: #6c757d;
}

.recording-controls {
    display: flex;
    justify-content: center;
    gap: 10px;
    flex-wrap: wrap;
}

.manual-controls {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}


.form-check-input:checked {
    background-color: #28a745;
    border-color: #28a745;
}

.header-controls {
    gap: 15px;
}

.control-section {
    background-color: #ffffff;
    padding: 15px;
    border-radius: 6px;
    border: 1px solid #e9ecef;
}

.camera-selection-section {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}

.recording-controls-section {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}

.disabled-section {
    opacity: 0.6;
    pointer-events: none;
}

.test-info .alert {
    background-color: #e3f2fd;
    border-color: #bbdefb;
    color: #0d47a1;
}

.connection-status .badge {
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
}

.connection-status .badge .fa-circle {
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
}

.recording-pulse {
    animation: recording-blink 1s infinite;
    color: #dc3545 !important;
}

@keyframes recording-blink {
    0% { opacity: 1; }
    50% { opacity: 0.3; }
    100% { opacity: 1; }
}

.swal-wide {
    width: 600px !important;
}

@media (max-width: 768px) {
    .header-controls {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        margin-top: 10px;
    }
    
    .connection-status {
        margin-right: 0 !important;
        margin-bottom: 10px;
    }
    
    .recording-controls {
        width: 100%;
    }
    
    .recording-controls .btn {
        width: 100%;
    }
    
    .form-check {
        margin-bottom: 10px;
    }
}
</style>